// ============================================================================
// G06 Multi-IGCC/RCC page
// ----------------------------------------------------------------------------
// Companion page to G06.jsx (Generate Master Curve). Where G06.jsx pools
// every RCC column into a single master fit, this page treats each *Assay
// column* as an independent G06 run (RCC fit + optional IGCC re-calibration
// + unknown evaluation) so QAT can process many lots at once side-by-side.
//
// Layout mirrors the QAT-supplied "G06 RCC IGCC Format.xlsx" reference:
//   Column A: shared Ref Cal Conc ladder (10 levels, defined by QAT).
//   Columns B..N: one Assay per column. Each Assay has its own:
//     - Master Curve ALU (10 rows; defined by MGCC, used by IGCC)
//     - Reference Calibrator ALU (10 rows; observed RCC standards)
//     - IGCC Calibrator ALU (3 rows; observed)
//     - IGCC Calibrator Derived Conc (3 rows; defined by MGCC)
//     - Unknown ALU input (up to 20 rows)
//     - Unknown Conc input (up to 20 rows; for back-predicting)
//   And produces per-Assay:
//     - Curve params a/b/c/d/n, fit_factor, shape_factor
//     - Unknown Fitted Conc (from Unknown ALU)
//     - Unknown Fitted ALU (from Unknown Conc)
//
// Backend reuse: every "Process Assay N" call posts the same /fitpooled
// payload the existing G06 page sends - {concs, rccSignals: [singleRccColumn],
// igccConcs, igccSignals, unknownAlus, unknownDoses}. No backend work is
// needed; this page just multiplexes that single-RCC fit across N columns.
// ============================================================================
import React, { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "../../utils/constants";
import { describeG06Status } from "../../utils/g06ErrorMessages";
import { G06BlockDataGrid } from "../Common/G06BlockDataGrid";

// ----- Constants matching the Excel reference layout -----
const REF_CAL_LEVELS = 10;       // Ref Cal Conc rows 1-10
const MASTER_ALU_LEVELS = 10;    // Master Curve ALU rows 1-10
const RCC_ALU_LEVELS = 10;       // Reference Calibrator ALU rows 1-10
const IGCC_LEVELS = 3;           // Calibrator ALU + Derived Conc rows 1-3
const UNKNOWN_ALU_LEVELS = 20;   // Unknown ALU rows 1-20 (Excel goes 1-19+infinite, we use 20)
const UNKNOWN_CONC_LEVELS = 20;  // Unknown Conc rows 1-20

const DEFAULT_ASSAY_COUNT = 10;
const MIN_ASSAY_COUNT = 1;
const MAX_ASSAY_COUNT = 50;

const API_BASE = (API_BASE_URL || "").replace(/\/+$/, "") + "/";
const RAWCURVE_POOLED_URL = `${API_BASE}api/v1/rawcurve/fitpooled`;

// ----- Small utilities (duplicated from G06.jsx so the two pages stay
// independent; both are short and stable.) -----
const parseMaybeNumber = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const s = String(v).trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

const formatWithRounding = (v, significantDigits, precisionDigits) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    if (n === 0) return n.toFixed(precisionDigits);
    const magnitude = Math.floor(Math.log10(Math.abs(n))) + 1;
    const scale = Math.pow(10, significantDigits - magnitude);
    const rounded = Math.round(n * scale) / scale;
    return rounded.toFixed(precisionDigits);
};

// Build a fresh Assay slot. Every cell is an empty string so MUI TextField
// values stay controlled regardless of what the user types or pastes.
// Each assay carries its own ref-cal ladder so it can be processed
// independently of the others.
const makeEmptyAssay = (idx) => ({
    name: `Assay ${idx + 1}`,
    refCalConc: Array(REF_CAL_LEVELS).fill(""),
    masterAlu: Array(MASTER_ALU_LEVELS).fill(""),
    rccAlu: Array(RCC_ALU_LEVELS).fill(""),
    igccAlu: Array(IGCC_LEVELS).fill(""),
    igccConc: Array(IGCC_LEVELS).fill(""),
    unknownAlu: Array(UNKNOWN_ALU_LEVELS).fill(""),
    unknownConc: Array(UNKNOWN_CONC_LEVELS).fill(""),
    // Outputs - all rendered as strings (formatted via formatWithRounding)
    fittedDoseFromAlu: Array(UNKNOWN_ALU_LEVELS).fill(""),
    fittedAluFromDose: Array(UNKNOWN_CONC_LEVELS).fill(""),
    params: { a: "", b: "", c: "", d: "", n: "" },
    fitFactor: "",
    shapeFactor: "",
    igccApplied: false,
    status: "" // "ok" | "error" | "" (untouched)
});

const makeEmptyAssayList = (n) =>
    Array.from({ length: n }, (_, i) => makeEmptyAssay(i));

// AssaySectionGrid wraps G06BlockDataGrid so the Multi-IGCC page can edit
// one input section (e.g. Ref Cal Conc, Master Curve ALU, Unknown ALU) as a
// single Excel-style block where rows are sample/level numbers and columns
// are assays. Copy/paste a rectangular block from Excel and it overlays
// onto the assay grid starting at the selected cell, just like the master-
// curve page's RCC table.
//
// Props:
//   sectionKey  - which assay field to read/write ("rccAlu", "igccAlu", ...)
//   levels      - row count for this section
//   assays      - full assay array (parent state)
//   rowLabel    - "Level" | "Cal" | "#" - used in the leftmost label column
//   labelTitle  - leftmost column header (shown in the grid header row)
//   onCellsChange - called with (sectionKey, rowIdx, assayIdx, value)
//                   for every cell that actually changed.  Single signal
//                   for both single-cell edits and multi-cell pastes.
const AssaySectionGrid = React.memo(({
    sectionKey, levels, assays, rowLabel, labelTitle, onCellsChange
}) => {
    // Build the columns: one fixed label column + one column per assay.
    // The label column is non-editable so paste lands on the first assay
    // column (column index 1) unless the user explicitly clicks an assay
    // cell first - matching the master-curve grid's "Ref" column behavior.
    const columnDefs = useMemo(() => {
        const cols = [{
            key: "__label",
            name: labelTitle,
            width: 70,
            editable: false
        }];
        for (let ai = 0; ai < assays.length; ai++) {
            cols.push({
                key: `a${ai}`,
                name: assays[ai].name || `Assay ${ai + 1}`,
                minWidth: 90
            });
        }
        return cols;
    }, [assays, labelTitle]);

    // Convert assay state -> grid rows. Each row is one level, each
    // assay's value lives at key `a${assayIdx}`.
    const rows = useMemo(() => Array.from({ length: levels }, (_, rowIdx) => {
        const r = { id: rowIdx, __label: `${rowLabel} ${rowIdx + 1}` };
        for (let ai = 0; ai < assays.length; ai++) {
            r[`a${ai}`] = assays[ai][sectionKey][rowIdx] ?? "";
        }
        return r;
    }), [assays, levels, sectionKey, rowLabel]);

    // Diff old vs new and fire one onCellsChange per changed cell so the
    // parent's assay state can be updated in a single batched setState.
    const handleDataChange = useCallback((newRows) => {
        const changes = [];
        for (let rowIdx = 0; rowIdx < levels; rowIdx++) {
            const newRow = newRows[rowIdx];
            if (!newRow) continue;
            for (let ai = 0; ai < assays.length; ai++) {
                const key = `a${ai}`;
                const prevVal = assays[ai][sectionKey][rowIdx] ?? "";
                const nextVal = newRow[key] ?? "";
                if (String(prevVal) !== String(nextVal)) {
                    changes.push({ assayIdx: ai, rowIdx, value: nextVal });
                }
            }
        }
        if (changes.length > 0) onCellsChange(sectionKey, changes);
    }, [assays, levels, sectionKey, onCellsChange]);

    return (
        <G06BlockDataGrid
            columnDefs={columnDefs}
            data={rows}
            rowCount={levels}
            onDataChange={handleDataChange}
        />
    );
});

const G06MultiIgcc = () => {
    // Operation banner / busy state, identical pattern to G06.jsx.
    const [operationStatus, setOperationStatus] = useState({
        message: "Ready",
        busy: false,
        level: "info",
        at: new Date()
    });
    const startOperation = useCallback((message) => {
        setOperationStatus({ message, busy: true, level: "info", at: new Date() });
    }, []);
    const finishOperation = useCallback((message, level = "success") => {
        setOperationStatus({ message, busy: false, level, at: new Date() });
    }, []);

    // Per-assay data table. Each assay carries its own ref-cal ladder, so
    // the page is just an array of self-contained G06 runs.
    const [assays, setAssays] = useState(() => makeEmptyAssayList(DEFAULT_ASSAY_COUNT));

    // Currently-focused assay (chart preview, single-process button, IGCC
    // chart toggle). Index into assays[].
    const [activeAssay, setActiveAssay] = useState(0);

    // Rounding settings - shared across the whole page so every assay's
    // formatted output uses the same precision.
    const [rounding, setRounding] = useState({
        significantDigits: 4,
        precisionDigits: 3,
        cvSignificantDigits: 3,
        cvPrecisionDigits: 1
    });

    // Section open/close toggles.
    const [inputsOpen, setInputsOpen] = useState(true);
    const [outputsOpen, setOutputsOpen] = useState(true);
    const [chartOpen, setChartOpen] = useState(true);

    // Chart label customizers - mirror the master-curve page so the user
    // can tweak the chart heading and axis labels right before exporting
    // a screenshot. Empty input falls back to the defaults so the chart
    // never renders a blank label.
    const [chartTitle, setChartTitle] = useState("Dose Curve Response");
    const [xAxisLabel, setXAxisLabel] = useState("Concentration");
    const [yAxisLabel, setYAxisLabel] = useState("Signal (ALU)");

    // Hovered point for the on-chart tooltip + cross-hair guides.
    const [hoveredPoint, setHoveredPoint] = useState(null);

    // File input refs for import.
    const fileInputRef = useRef(null);

    // Curve points cache for the chart preview - one entry per assay.
    const [curvePointsByAssay, setCurvePointsByAssay] = useState({});

    // Keep activeAssay in range when the user shrinks the assay count.
    React.useEffect(() => {
        if (activeAssay >= assays.length) {
            setActiveAssay(Math.max(0, assays.length - 1));
        }
    }, [assays.length, activeAssay]);

    // ----- Cell editors. -----
    const setAssayName = useCallback((assayIdx, value) => {
        setAssays(prev => {
            const copy = [...prev];
            copy[assayIdx] = { ...copy[assayIdx], name: value };
            return copy;
        });
    }, []);

    // Batched cell setter used by AssaySectionGrid. Applies a list of
    // {assayIdx, rowIdx, value} changes in one setState so a multi-cell
    // paste turns into a single React render. Stable identity so the
    // section grids don't churn between renders.
    const setAssayCellsBatch = useCallback((sectionKey, changes) => {
        if (!changes || changes.length === 0) return;
        setAssays(prev => {
            const copy = [...prev];
            // Group changes by assay to avoid spreading the same assay
            // multiple times when several rows of one column changed.
            const byAssay = new Map();
            for (const ch of changes) {
                if (!byAssay.has(ch.assayIdx)) byAssay.set(ch.assayIdx, []);
                byAssay.get(ch.assayIdx).push(ch);
            }
            for (const [ai, list] of byAssay) {
                const a = { ...copy[ai] };
                const arr = [...a[sectionKey]];
                for (const { rowIdx, value } of list) arr[rowIdx] = value;
                a[sectionKey] = arr;
                copy[ai] = a;
            }
            return copy;
        });
    }, []);

    const handleClearAll = () => {
        setAssays(makeEmptyAssayList(assays.length));
        setCurvePointsByAssay({});
        finishOperation("All assay data cleared.", "info");
    };

    // ----- Per-assay processing -----
    // Builds the same payload shape G06.jsx sends to /fitpooled, but for one
    // single assay column. mode controls which inputs the backend uses:
    //   "rcc"  - plain protein-5 fit. Inputs per the QAT reference:
    //              Ref Cal Conc + Reference Calibrator ALU + Unknown ALU
    //              + Unknown Conc. Master Curve ALU is ignored.
    //   "igcc" - bend the existing master curve against calibrator pairs.
    //            Inputs per the QAT reference:
    //              Ref Cal Conc + Master Curve ALU + Calibrator ALU
    //              + Calibrator Derived Conc + Unknown ALU + Unknown Conc.
    //            The reference signals fed to /fitpooled are the user's
    //            Master Curve ALU column (not the RCC readings) - IGCC
    //            re-anchors a defined curve, it doesn't re-derive one.
    // Returns { ok, reason?, response? } so callers can accumulate results
    // without network errors short-circuiting the whole batch.
    const processOneAssay = useCallback(async (assayIdx, mode = "igcc") => {
        const assay = assays[assayIdx];
        if (!assay) return { ok: false, reason: "missing assay" };

        // RCC mode reads from rccAlu (Reference Calibrator ALU); IGCC mode
        // reads from masterAlu (Master Curve ALU defined by MGCC).
        const referenceColumn = mode === "igcc" ? assay.masterAlu : assay.rccAlu;
        const referenceLabel = mode === "igcc" ? "Master Curve ALU" : "Reference Calibrator ALU";

        const concs = [];
        const refSig = [];
        for (let i = 0; i < REF_CAL_LEVELS; i++) {
            const c = parseMaybeNumber(assay.refCalConc[i]);
            const y = parseMaybeNumber(referenceColumn[i]);
            if (c !== null && y !== null) {
                concs.push(c);
                refSig.push(y);
            }
        }
        if (concs.length < 5) {
            return { ok: false, reason: `only ${concs.length} valid ${referenceLabel} rows paired with Ref Cal Conc (need 5+)` };
        }

        // IGCC pairs only get sent in "igcc" mode; "rcc" mode forces the
        // backend onto the plain RCC fit even if the user already typed
        // calibrator pairs into the IGCC inputs.
        const igccPairs = [];
        if (mode === "igcc") {
            for (let i = 0; i < IGCC_LEVELS; i++) {
                const a = parseMaybeNumber(assay.igccAlu[i]);
                const d = parseMaybeNumber(assay.igccConc[i]);
                if (a !== null && d !== null) igccPairs.push({ alu: a, dose: d });
            }
        }

        const unknownAlus = assay.unknownAlu.map(v => {
            const n = parseMaybeNumber(v);
            return n === null ? NaN : n;
        });
        const unknownDoses = assay.unknownConc.map(v => {
            const n = parseMaybeNumber(v);
            return n === null ? NaN : n;
        });

        const payload = {
            assay: 1,
            concs,
            rccSignals: [refSig],
            igccConcs: igccPairs.map(p => p.dose),
            igccSignals: igccPairs.map(p => p.alu),
            unknownAlus,
            unknownDoses
        };

        try {
            const resp = await axios.post(
                RAWCURVE_POOLED_URL,
                payload,
                { headers: { "Content-Type": "application/json" }, timeout: 30000 }
            );
            const data = resp.data;
            if (data?.status !== 1) {
                const detail = describeG06Status(data?.status, data?.message);
                return { ok: false, reason: `backend status ${data?.status ?? "unknown"} - ${detail}` };
            }
            return { ok: true, response: data, igccPairCount: igccPairs.length, mode };
        } catch (err) {
            return { ok: false, reason: err?.message || "request failed" };
        }
    }, [assays]);

    // Apply a successful response back into the assay's output cells.
    const applyResponseToAssay = (assayIdx, data) => {
        const sig = rounding.significantDigits;
        const prec = rounding.precisionDigits;
        const fmt = (v) => formatWithRounding(v, sig, prec);

        setAssays(prev => {
            const copy = [...prev];
            const a = { ...copy[assayIdx] };

            const params = Array.isArray(data?.params) && data.params.length >= 5 ? data.params : null;
            a.params = params ? {
                a: fmt(params[0]), b: fmt(params[1]), c: fmt(params[2]),
                d: fmt(params[3]), n: fmt(params[4])
            } : { a: "", b: "", c: "", d: "", n: "" };
            a.fitFactor = data?.fit_factor != null ? fmt(data.fit_factor) : "";
            a.shapeFactor = (data?.igcc_applied && data?.igcc_shape_factor != null)
                ? fmt(data.igcc_shape_factor)
                : (data?.shape_factor != null ? fmt(data.shape_factor) : "");
            a.igccApplied = !!data?.igcc_applied;
            a.status = "ok";

            const unkDose = Array.isArray(data?.unknown_doses_from_alu) ? data.unknown_doses_from_alu : [];
            const unkAlu = Array.isArray(data?.unknown_alus_from_dose) ? data.unknown_alus_from_dose : [];

            a.fittedDoseFromAlu = a.unknownAlu.map((src, i) => {
                if (parseMaybeNumber(src) === null) return "";
                const v = unkDose[i];
                return (v == null || !Number.isFinite(Number(v))) ? "" : fmt(v);
            });
            a.fittedAluFromDose = a.unknownConc.map((src, i) => {
                if (parseMaybeNumber(src) === null) return "";
                const v = unkAlu[i];
                return (v == null || !Number.isFinite(Number(v))) ? "" : fmt(v);
            });

            // Note: Master Curve ALU is intentionally NOT auto-populated
            // from pooled_signals. This page processes each assay column
            // as an independent single-RCC fit, so there is no genuine
            // pooled-MGCC ALU to seed - it's just an echo of the input
            // signals. Leave masterAlu alone so the user explicitly
            // pastes/enters the MGCC-defined column when they want to
            // run an IGCC bend.

            copy[assayIdx] = a;
            return copy;
        });

        // Cache curve points for the chart.
        const curvePts = Array.isArray(data?.curve_points)
            ? data.curve_points
                .map(p => ({ x: Number(p.x), y: Number(p.y) }))
                .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y))
            : [];
        setCurvePointsByAssay(prev => ({ ...prev, [assayIdx]: curvePts }));
    };

    const markAssayError = (assayIdx, reason) => {
        setAssays(prev => {
            const copy = [...prev];
            copy[assayIdx] = { ...copy[assayIdx], status: "error", fitFactor: "", shapeFactor: "" };
            return copy;
        });
        setCurvePointsByAssay(prev => {
            const copy = { ...prev };
            delete copy[assayIdx];
            return copy;
        });
        console.warn(`Assay ${assayIdx + 1} failed: ${reason}`);
    };

    const handleCalculateActive = async (mode) => {
        const label = mode === "rcc" ? "RCC" : "IGCC";
        startOperation(`Calculating ${label} for ${assays[activeAssay].name}...`);
        const res = await processOneAssay(activeAssay, mode);
        if (res.ok) {
            applyResponseToAssay(activeAssay, res.response);
            const tail = res.response?.igcc_applied
                ? ` (IGCC bent with ${res.igccPairCount} re-calibrator${res.igccPairCount === 1 ? "" : "s"})`
                : (mode === "igcc" ? " (no valid IGCC pairs - plain RCC fit)" : "");
            finishOperation(`${assays[activeAssay].name} ${label} calculation complete${tail}.`, "success");
        } else {
            markAssayError(activeAssay, res.reason);
            finishOperation(`${assays[activeAssay].name} ${label} failed: ${res.reason}.`, "error");
        }
    };

    const handleProcessAllAssays = async (mode) => {
        const label = mode === "rcc" ? "RCC" : "IGCC";
        // Skip empty assays - if every input cell is blank we don't waste
        // a backend call. Detected by "no rcc_alu values entered".
        const populatedIdx = assays
            .map((a, i) => ({ a, i }))
            .filter(({ a }) => a.rccAlu.some(v => parseMaybeNumber(v) !== null))
            .map(({ i }) => i);

        if (populatedIdx.length === 0) {
            finishOperation("No populated assays to process.", "warning");
            return;
        }

        let okCount = 0, errCount = 0, bentCount = 0;
        for (let k = 0; k < populatedIdx.length; k++) {
            const idx = populatedIdx[k];
            startOperation(`Calculating ${label} for ${assays[idx].name} (${k + 1} of ${populatedIdx.length})...`);
            // eslint-disable-next-line no-await-in-loop
            const res = await processOneAssay(idx, mode);
            if (res.ok) {
                applyResponseToAssay(idx, res.response);
                okCount++;
                if (res.response?.igcc_applied) bentCount++;
            } else {
                markAssayError(idx, res.reason);
                errCount++;
            }
        }
        finishOperation(
            `${label}: processed ${okCount} of ${populatedIdx.length} assay${populatedIdx.length === 1 ? "" : "s"} (${bentCount} with IGCC bend, ${errCount} failed).`,
            errCount > 0 ? "warning" : "success"
        );
    };

    // ----- Excel export (one sheet per assay + summary). -----
    const handleExportToExcel = async () => {
        try {
            startOperation("Building Excel export...");
            const exportedAt = new Date();
            const stampForFile = exportedAt.toISOString().replace(/[:.]/g, "-").slice(0, 19);
            const wb = new ExcelJS.Workbook();
            wb.creator = "G06 Multi-IGCC/RCC";
            wb.created = exportedAt;

            // Summary sheet: one row per assay with key outputs.
            const wsSum = wb.addWorksheet("Summary");
            const sumHeaders = [
                "Assay", "Status", "Fit Factor", "Shape Factor",
                "a", "b", "c", "d", "n",
                "IGCC Applied", "Unk ALU rows", "Unk Dose rows"
            ];
            for (let i = 0; i < sumHeaders.length; i++) {
                const c = wsSum.getCell(1, i + 1);
                c.value = sumHeaders[i];
                c.font = { bold: true };
            }
            assays.forEach((a, i) => {
                const r = i + 2;
                wsSum.getCell(r, 1).value = a.name;
                wsSum.getCell(r, 2).value = a.status || "(not run)";
                wsSum.getCell(r, 3).value = a.fitFactor;
                wsSum.getCell(r, 4).value = a.shapeFactor;
                wsSum.getCell(r, 5).value = a.params.a;
                wsSum.getCell(r, 6).value = a.params.b;
                wsSum.getCell(r, 7).value = a.params.c;
                wsSum.getCell(r, 8).value = a.params.d;
                wsSum.getCell(r, 9).value = a.params.n;
                wsSum.getCell(r, 10).value = a.igccApplied ? "Yes" : "No";
                wsSum.getCell(r, 11).value = a.unknownAlu.filter(v => parseMaybeNumber(v) !== null).length;
                wsSum.getCell(r, 12).value = a.unknownConc.filter(v => parseMaybeNumber(v) !== null).length;
            });
            for (let c = 1; c <= sumHeaders.length; c++) wsSum.getColumn(c).width = 14;

            // One detail sheet per assay - mirrors the Excel reference layout.
            assays.forEach((a, idx) => {
                const ws = wb.addWorksheet(a.name.slice(0, 31)); // 31-char sheet name limit
                const writeBlock = (startRow, title, headers, rows) => {
                    let r = startRow;
                    if (title) {
                        const c = ws.getCell(r, 1);
                        c.value = title; c.font = { bold: true };
                        r++;
                    }
                    if (headers) {
                        for (let i = 0; i < headers.length; i++) {
                            const c = ws.getCell(r, i + 1);
                            c.value = headers[i]; c.font = { bold: true };
                        }
                        r++;
                    }
                    for (const row of rows) {
                        for (let i = 0; i < row.length; i++) {
                            ws.getCell(r, i + 1).value = row[i];
                        }
                        r++;
                    }
                    return r + 1;
                };

                let r = 1;
                r = writeBlock(r, `${a.name} - Inputs`, null, []);
                r = writeBlock(r, "Ref Cal Conc & RCC ALU & Master ALU",
                    ["Level", "Ref Cal Conc", "Master Curve ALU", "RCC ALU"],
                    Array.from({ length: REF_CAL_LEVELS }, (_, i) => [
                        i + 1, a.refCalConc[i], a.masterAlu[i], a.rccAlu[i]
                    ]));
                r = writeBlock(r, "IGCC Calibrators",
                    ["#", "ALU", "Derived Conc"],
                    Array.from({ length: IGCC_LEVELS }, (_, i) => [
                        i + 1, a.igccAlu[i], a.igccConc[i]
                    ]));
                r = writeBlock(r, "Unknowns: Input ALU -> Fitted Dose",
                    ["#", "Input ALU", "Fitted Dose"],
                    Array.from({ length: UNKNOWN_ALU_LEVELS }, (_, i) => [
                        i + 1, a.unknownAlu[i], a.fittedDoseFromAlu[i]
                    ]));
                r = writeBlock(r, "Unknowns: Input Conc -> Fitted ALU",
                    ["#", "Input Dose", "Fitted ALU"],
                    Array.from({ length: UNKNOWN_CONC_LEVELS }, (_, i) => [
                        i + 1, a.unknownConc[i], a.fittedAluFromDose[i]
                    ]));
                r = writeBlock(r, "Curve Parameters",
                    ["a", "b", "c", "d", "n", "Fit Factor", "Shape Factor", "IGCC Applied"],
                    [[a.params.a, a.params.b, a.params.c, a.params.d, a.params.n,
                      a.fitFactor, a.shapeFactor, a.igccApplied ? "Yes" : "No"]]);

                // Curve points if available
                const cp = curvePointsByAssay[idx] || [];
                if (cp.length > 0) {
                    writeBlock(r, "Curve Points",
                        ["Dose", "Signal"],
                        cp.map(p => [p.x.toFixed(4), p.y.toFixed(4)]));
                }

                for (let c = 1; c <= 8; c++) ws.getColumn(c).width = 14;
            });

            const buf = await wb.xlsx.writeBuffer();
            saveAs(
                new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
                `G06_MultiIGCC_${stampForFile}.xlsx`
            );
            finishOperation(`Workbook exported: 1 summary + ${assays.length} assay sheets.`, "success");
        } catch (err) {
            console.error(err);
            finishOperation(`Export failed: ${err?.message || err}`, "error");
        }
    };

    // ----- Excel import (auto-fills the grid from a sheet matching the
    // QAT reference format. Tolerant about which row each block starts on
    // by scanning for known section labels.) -----
    const handleImportFromExcel = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        startOperation(`Importing ${file.name}...`);
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(new Uint8Array(buf), { type: "array" });
            const sheetName = wb.SheetNames.find(n => /multi/i.test(n)) || wb.SheetNames[0];
            const ws = wb.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

            // Section labels from the reference - column F holds the label.
            const labelCol = 5; // column F (0-indexed)
            const firstAssayCol = 7; // column H = Assay 1

            const findRow = (needle) => rows.findIndex(r =>
                String(r[labelCol] ?? "").toLowerCase().includes(needle)
            );

            const refRow = findRow("ref cal conc");
            const masterRow = findRow("master curve alu");
            const rccRow = findRow("reference calibrator alu");
            const calAluRow = findRow("calibrator alu");
            const calDoseRow = findRow("calibrator derived");
            const unkAluRow = findRow("unkown alu") >= 0 ? findRow("unkown alu") : findRow("unknown alu");
            const unkConcRow = findRow("unknown conc");

            if (refRow < 0) {
                finishOperation("Import failed: could not find 'Ref Cal Conc' label in column F.", "error");
                return;
            }

            // Detect how many assay columns the sheet defines.
            const headerRow = rows[4] || []; // row 5 = headers (Assay 1, Assay 2, ...)
            let numAssays = 0;
            for (let c = firstAssayCol; c < headerRow.length; c++) {
                if (String(headerRow[c] ?? "").toLowerCase().includes("assay")) numAssays++;
                else break;
            }
            numAssays = Math.max(MIN_ASSAY_COUNT, Math.min(MAX_ASSAY_COUNT, numAssays || DEFAULT_ASSAY_COUNT));

            // New ref-cal column.
            const newRefCal = Array(REF_CAL_LEVELS).fill("");
            for (let i = 0; i < REF_CAL_LEVELS; i++) {
                const r = rows[refRow + i];
                if (r) {
                    // Reference values are sometimes in the column right after
                    // the level number. Try both layouts.
                    const candidate = parseMaybeNumber(r[firstAssayCol - 1]);
                    newRefCal[i] = candidate !== null ? String(candidate) : (r[firstAssayCol - 1] ?? "");
                }
            }

            // Build new assays.
            const readBlock = (startRow, count, assayIdx) => {
                const out = Array(count).fill("");
                for (let i = 0; i < count; i++) {
                    const r = rows[startRow + i];
                    if (!r) continue;
                    const v = r[firstAssayCol + assayIdx];
                    if (v !== "" && v !== null && v !== undefined) out[i] = String(v);
                }
                return out;
            };

            const newAssays = Array.from({ length: numAssays }, (_, ai) => {
                const a = makeEmptyAssay(ai);
                a.name = String(headerRow[firstAssayCol + ai] || a.name);
                // The reference layout has a single Ref Cal Conc column shared
                // across assays; broadcast it into every assay's per-assay
                // ladder so the user can still tweak it independently after
                // import if they want.
                a.refCalConc = [...newRefCal];
                if (masterRow >= 0)  a.masterAlu  = readBlock(masterRow, MASTER_ALU_LEVELS, ai);
                if (rccRow >= 0)     a.rccAlu     = readBlock(rccRow, RCC_ALU_LEVELS, ai);
                if (calAluRow >= 0)  a.igccAlu    = readBlock(calAluRow, IGCC_LEVELS, ai);
                if (calDoseRow >= 0) a.igccConc   = readBlock(calDoseRow, IGCC_LEVELS, ai);
                if (unkAluRow >= 0)  a.unknownAlu = readBlock(unkAluRow, UNKNOWN_ALU_LEVELS, ai);
                if (unkConcRow >= 0) a.unknownConc = readBlock(unkConcRow, UNKNOWN_CONC_LEVELS, ai);
                return a;
            });

            setAssays(newAssays);
            setActiveAssay(0);
            setCurvePointsByAssay({});
            finishOperation(`Imported ${numAssays} assay column${numAssays === 1 ? "" : "s"} from ${sheetName}.`, "success");
        } catch (err) {
            console.error(err);
            finishOperation(`Import failed: ${err?.message || err}`, "error");
        }
    };

    // ----- Chart preview for the active assay. -----
    const activeCurvePts = curvePointsByAssay[activeAssay] || [];
    const activeRawPts = useMemo(() => {
        const out = [];
        const a = assays[activeAssay];
        if (!a) return out;
        for (let i = 0; i < REF_CAL_LEVELS; i++) {
            const x = parseMaybeNumber(a.refCalConc[i]);
            const y = parseMaybeNumber(a.rccAlu[i]);
            if (x !== null && y !== null) out.push({ x, y });
        }
        return out;
    }, [assays, activeAssay]);

    // IGCC pseudocal points (Dose, ALU) for the chart - rendered as purple
    // crosses, only after a successful IGCC bend. Before that the inputs
    // are still being typed and shouldn't pollute the chart.
    const activeIgccPts = useMemo(() => {
        const a = assays[activeAssay];
        if (!a || !a.igccApplied) return [];
        const out = [];
        for (let i = 0; i < IGCC_LEVELS; i++) {
            const x = parseMaybeNumber(a.igccConc[i]);
            const y = parseMaybeNumber(a.igccAlu[i]);
            if (x !== null && y !== null) out.push({ x, y, label: `CAL${i + 1}` });
        }
        return out;
    }, [assays, activeAssay]);

    // ----- Render helpers -----
    const renderReadonlyCellGrid = (values, levels) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {Array.from({ length: levels }, (_, rowIdx) => (
                <TextField
                    key={rowIdx}
                    size="small"
                    value={values[rowIdx] ?? ""}
                    InputProps={{ readOnly: true }}
                    sx={{
                        "& .MuiInputBase-input": {
                            py: 0.25, fontSize: 11, textAlign: "right",
                            backgroundColor: "#f5f5f5"
                        },
                        "& .MuiOutlinedInput-root": { height: 22 }
                    }}
                />
            ))}
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>G06 Multi-IGCC/RCC</Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                Process many assays side-by-side. Each column is an independent G06 run with its own
                Reference Calibrator ALU, IGCC pairs, and unknowns. Use <b>Process All Assays</b> to
                fit every populated column at once.
            </Typography>

            {/* ----- Action bar -----
                Two pairs of buttons let the user run a plain RCC fit or an
                IGCC-bent fit on either the active assay or every populated
                assay. The IGCC variant only diverges from RCC when the
                active assay actually has IGCC pairs filled in. */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
                <Button variant="contained" color="primary"
                    onClick={() => handleCalculateActive("rcc")}
                    disabled={operationStatus.busy}
                    title={`Plain RCC (protein-5) fit on ${assays[activeAssay]?.name || "the active assay"} - ignores IGCC pairs even if filled in.`}>
                    Calculate RCC ({assays[activeAssay]?.name || "Active"})
                </Button>
                <Button variant="contained" color="secondary"
                    onClick={() => handleCalculateActive("igcc")}
                    disabled={operationStatus.busy}
                    title={`Fit ${assays[activeAssay]?.name || "the active assay"} and bend the curve against any IGCC pairs that are filled in.`}>
                    Calculate IGCC ({assays[activeAssay]?.name || "Active"})
                </Button>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Button variant="outlined" color="primary"
                    onClick={() => handleProcessAllAssays("rcc")}
                    disabled={operationStatus.busy}
                    title="Plain RCC fit on every populated assay column.">
                    Calculate RCC (All)
                </Button>
                <Button variant="outlined" color="secondary"
                    onClick={() => handleProcessAllAssays("igcc")}
                    disabled={operationStatus.busy}
                    title="IGCC-bent fit on every populated assay column.">
                    Calculate IGCC (All)
                </Button>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" sx={{ mx: 1, color: "text.secondary" }}>
                    {assays.length} assay{assays.length === 1 ? "" : "s"}
                </Typography>
                <Button variant="outlined" color="warning" onClick={handleClearAll}
                    title="Clear every assay's inputs and outputs.">
                    Clear All
                </Button>
                <Button variant="outlined" onClick={() => fileInputRef.current?.click()}
                    title="Auto-fill the grid from an Excel sheet matching the QAT reference layout.">
                    Import from Excel
                </Button>
                <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    ref={fileInputRef}
                    onChange={handleImportFromExcel}
                    style={{ display: "none" }}
                />
                <Button variant="outlined" onClick={handleExportToExcel}
                    disabled={operationStatus.busy}
                    title="Save a workbook with one summary sheet plus one detail sheet per assay.">
                    Export to Excel
                </Button>
            </Box>

            {/* ----- Status banner ----- */}
            <Alert
                severity={
                    operationStatus.level === "error" ? "error"
                    : operationStatus.level === "warning" ? "warning"
                    : operationStatus.level === "success" ? "success"
                    : "info"
                }
                icon={operationStatus.busy
                    ? <CircularProgress size={16} />
                    : operationStatus.level === "error" ? <ErrorOutlineIcon fontSize="small" />
                    : operationStatus.level === "warning" ? <WarningAmberIcon fontSize="small" />
                    : operationStatus.level === "success" ? <CheckCircleOutlineIcon fontSize="small" />
                    : undefined}
                sx={{ mb: 2 }}
            >
                {operationStatus.busy ? "Working... " : ""}{operationStatus.message}
                {operationStatus.at && (
                    <Box component="span" sx={{ ml: 1.5, opacity: 0.7, fontFamily: "monospace", fontSize: "0.85em" }}>
                        [{operationStatus.at.toLocaleString()}]
                    </Box>
                )}
            </Alert>

            {/* ----- Rounding ----- */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField label="Sig Digits" type="number" size="small"
                    value={rounding.significantDigits}
                    onChange={(e) => setRounding({ ...rounding, significantDigits: Number(e.target.value) })} />
                <TextField label="Precision" type="number" size="small"
                    value={rounding.precisionDigits}
                    onChange={(e) => setRounding({ ...rounding, precisionDigits: Number(e.target.value) })} />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ----- Active-assay tab strip ----- */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1,
                px: 0.5, py: 0.5, backgroundColor: "#f5f5f5", borderRadius: 1
            }}>
                <Typography variant="caption" sx={{ alignSelf: "center", mr: 1, color: "text.secondary" }}>
                    Active Assay:
                </Typography>
                {assays.map((a, i) => (
                    <Button
                        key={`assay-tab-${i}`}
                        size="small"
                        variant={i === activeAssay ? "contained" : "outlined"}
                        onClick={() => setActiveAssay(i)}
                        sx={{
                            minWidth: 60, height: 26, p: 0, fontSize: 11,
                            lineHeight: 1, textTransform: "none",
                            // Color cue: green = ok, red = error, default = untouched
                            borderColor: a.status === "ok" ? "success.main"
                                : a.status === "error" ? "error.main"
                                : undefined
                        }}
                        title={a.status === "ok"
                            ? `${a.name}: fit complete${a.igccApplied ? " (IGCC bent)" : ""}.`
                            : a.status === "error"
                                ? `${a.name}: last process failed.`
                                : `${a.name}: not yet processed.`}
                    >
                        {a.name}
                    </Button>
                ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ----- Inputs grid -----
                One Excel-style block per section. Rows are sample/level
                numbers, columns are assays. Click any cell, paste a
                rectangular block from Excel, and it splatters across
                every assay in the paste range. Same widget the master-
                curve page uses for its RCC table. */}
            <Button onClick={() => setInputsOpen(!inputsOpen)}
                startIcon={inputsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 1 }}>
                Assay Inputs
            </Button>
            <Collapse in={inputsOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    {/* Editable assay-name row above the section grids. */}
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mb: 1 }}>
                        <Box sx={{ width: 70, fontSize: 11, fontWeight: 600, color: "text.secondary" }}>
                            Assay
                        </Box>
                        {assays.map((a, ai) => (
                            <TextField
                                key={`name-${ai}`}
                                size="small"
                                value={a.name}
                                onChange={(e) => setAssayName(ai, e.target.value)}
                                sx={{
                                    minWidth: 90, flex: "1 1 90px",
                                    "& .MuiInputBase-input": { py: 0.25, fontSize: 11, textAlign: "center", fontWeight: 600 },
                                    "& .MuiOutlinedInput-root": {
                                        height: 24,
                                        backgroundColor: ai === activeAssay ? "rgba(25,118,210,0.08)" : undefined
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>
                        Ref Cal Conc (per assay)
                    </Typography>
                    <AssaySectionGrid
                        sectionKey="refCalConc"
                        levels={REF_CAL_LEVELS}
                        assays={assays}
                        rowLabel="Level"
                        labelTitle="Ref"
                        onCellsChange={setAssayCellsBatch}
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>
                        Master Curve ALU (defined by MGCC)
                    </Typography>
                    <AssaySectionGrid
                        sectionKey="masterAlu"
                        levels={MASTER_ALU_LEVELS}
                        assays={assays}
                        rowLabel="Level"
                        labelTitle="Ref"
                        onCellsChange={setAssayCellsBatch}
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>
                        Reference Calibrator ALU (RCC)
                    </Typography>
                    <AssaySectionGrid
                        sectionKey="rccAlu"
                        levels={RCC_ALU_LEVELS}
                        assays={assays}
                        rowLabel="Level"
                        labelTitle="Ref"
                        onCellsChange={setAssayCellsBatch}
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>
                        IGCC Calibrator ALU
                    </Typography>
                    <AssaySectionGrid
                        sectionKey="igccAlu"
                        levels={IGCC_LEVELS}
                        assays={assays}
                        rowLabel="Cal"
                        labelTitle="Cal"
                        onCellsChange={setAssayCellsBatch}
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>
                        IGCC Calibrator Derived Conc
                    </Typography>
                    <AssaySectionGrid
                        sectionKey="igccConc"
                        levels={IGCC_LEVELS}
                        assays={assays}
                        rowLabel="Cal"
                        labelTitle="Cal"
                        onCellsChange={setAssayCellsBatch}
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>
                        Unknown ALU (back-predicts to Conc)
                    </Typography>
                    <AssaySectionGrid
                        sectionKey="unknownAlu"
                        levels={UNKNOWN_ALU_LEVELS}
                        assays={assays}
                        rowLabel="#"
                        labelTitle="#"
                        onCellsChange={setAssayCellsBatch}
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>
                        Unknown Conc (back-predicts to ALU)
                    </Typography>
                    <AssaySectionGrid
                        sectionKey="unknownConc"
                        levels={UNKNOWN_CONC_LEVELS}
                        assays={assays}
                        rowLabel="#"
                        labelTitle="#"
                        onCellsChange={setAssayCellsBatch}
                    />
                </Paper>
            </Collapse>

            {/* ----- Outputs grid ----- */}
            <Button onClick={() => setOutputsOpen(!outputsOpen)}
                startIcon={outputsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 1 }}>
                Assay Outputs
            </Button>
            <Collapse in={outputsOpen}>
                <Paper sx={{ p: 2, mb: 2, overflowX: "auto" }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", minWidth: 800 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 180, position: "sticky", left: 0, backgroundColor: "white", zIndex: 1 }}>
                            <Box sx={{ height: 22 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Curve Parameters</Typography>
                            {["a", "b", "c", "d", "n"].map(k => (
                                <Box key={`p-${k}`} sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>{k}</Box>
                            ))}
                            <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>Fit Stats</Typography>
                            <Box sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>Fit Factor</Box>
                            <Box sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>Shape Factor</Box>
                            <Box sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>IGCC Applied</Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>Fitted Dose (from Unknown ALU)</Typography>
                            {Array.from({ length: UNKNOWN_ALU_LEVELS }, (_, i) => (
                                <Box key={`fdose-${i}`} sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>#{i + 1}</Box>
                            ))}
                            <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>Fitted ALU (from Unknown Conc)</Typography>
                            {Array.from({ length: UNKNOWN_CONC_LEVELS }, (_, i) => (
                                <Box key={`falu-${i}`} sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>#{i + 1}</Box>
                            ))}
                        </Box>

                        {assays.map((a, ai) => (
                            <Box key={`assay-out-${ai}`} sx={{
                                display: "flex", flexDirection: "column", gap: 0.25,
                                minWidth: 110, borderLeft: "1px solid #e0e0e0", pl: 0.5,
                                backgroundColor: ai === activeAssay ? "rgba(25,118,210,0.04)" : undefined
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, textAlign: "center" }}>
                                    {a.name}
                                </Typography>
                                <Box sx={{ height: 18 }} />
                                {renderReadonlyCellGrid(
                                    [a.params.a, a.params.b, a.params.c, a.params.d, a.params.n], 5)}
                                <Box sx={{ height: 18 }} />
                                {renderReadonlyCellGrid(
                                    [a.fitFactor, a.shapeFactor, a.igccApplied ? "Yes" : (a.status === "ok" ? "No" : "")], 3)}
                                <Box sx={{ height: 18 }} />
                                {renderReadonlyCellGrid(a.fittedDoseFromAlu, UNKNOWN_ALU_LEVELS)}
                                <Box sx={{ height: 18 }} />
                                {renderReadonlyCellGrid(a.fittedAluFromDose, UNKNOWN_CONC_LEVELS)}
                            </Box>
                        ))}
                    </Box>
                </Paper>
            </Collapse>

            {/* ----- Chart preview for the active assay -----
                Same render as the master-curve page: axis ticks + gridlines,
                X/Y axis labels, fitted-curve polyline + dots, raw RCC reds,
                IGCC pseudocal purple crosses, top-right legend, and a
                hover tooltip with crosshair guides. The chart title and
                axis labels are user-customizable. */}
            <Button onClick={() => setChartOpen(!chartOpen)}
                startIcon={chartOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 1 }}>
                Chart Preview ({assays[activeAssay]?.name || "n/a"})
            </Button>
            <Collapse in={chartOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    {/* Customizers - same pattern as G06.jsx's chart-title input.
                        Empty input falls back to the default so the chart never
                        renders a blank label. */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
                        <TextField
                            label="Chart Title"
                            size="small"
                            value={chartTitle}
                            onChange={(e) => setChartTitle(e.target.value || "Dose Curve Response")}
                            sx={{ minWidth: 220 }}
                        />
                        <TextField
                            label="X Axis Label"
                            size="small"
                            value={xAxisLabel}
                            onChange={(e) => setXAxisLabel(e.target.value || "Concentration")}
                            sx={{ minWidth: 180 }}
                        />
                        <TextField
                            label="Y Axis Label"
                            size="small"
                            value={yAxisLabel}
                            onChange={(e) => setYAxisLabel(e.target.value || "Signal (ALU)")}
                            sx={{ minWidth: 180 }}
                        />
                    </Box>

                    {activeCurvePts.length === 0 ? (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Process this assay to populate the chart.
                        </Typography>
                    ) : (
                        <Box sx={{
                            width: 874, maxWidth: "100%", height: 540,
                            position: "relative",
                            border: "1px solid #ccc", borderRadius: 1,
                            backgroundColor: "#fafafa", p: 1
                        }}>
                            {/* "Currently viewing" banner mirrors the master page. */}
                            <Box sx={{
                                position: "absolute", top: 6, left: 10, zIndex: 3,
                                maxWidth: 360,
                                backgroundColor: "rgba(25,118,210,0.08)",
                                color: "#1976d2",
                                border: "1px solid rgba(25,118,210,0.3)",
                                borderRadius: 1, px: 1, py: 0.25,
                                fontSize: 11, fontWeight: 600, lineHeight: 1.25,
                                pointerEvents: "none"
                            }}>
                                Currently viewing: {assays[activeAssay].name}
                                {assays[activeAssay].igccApplied ? " (IGCC bent)" : ""}
                            </Box>

                            <svg width="100%" height="100%" viewBox="0 0 874 540" preserveAspectRatio="none">
                                {(() => {
                                    const all = [...activeCurvePts, ...activeRawPts, ...activeIgccPts];
                                    if (all.length === 0) return null;
                                    const xs = all.map(p => p.x), ys = all.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs);
                                    const minY = Math.min(...ys), maxY = Math.max(...ys);

                                    const leftPad = 70, rightPad = 20, topPad = 60, bottomPad = 50;
                                    const cw = 874 - leftPad - rightPad;
                                    const ch = 540 - topPad - bottomPad;
                                    const sx = (x) => leftPad + ((x - minX) / ((maxX - minX) || 1)) * cw;
                                    const sy = (y) => topPad + ch - ((y - minY) / ((maxY - minY) || 1)) * ch;

                                    const linePoints = activeCurvePts.map(p => `${sx(p.x)},${sy(p.y)}`).join(" ");
                                    const xTicks = Array.from({ length: 5 }, (_, i) => {
                                        const value = minX + ((maxX - minX) * i) / 4;
                                        return { value, x: sx(value) };
                                    });
                                    const yTicks = Array.from({ length: 5 }, (_, i) => {
                                        const value = minY + ((maxY - minY) * (4 - i)) / 4;
                                        return { value, y: sy(value) };
                                    });

                                    return (
                                        <>
                                            <text x={leftPad + cw / 2} y="42" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">
                                                {chartTitle}
                                            </text>

                                            {/* Axes */}
                                            <line x1={leftPad} y1={topPad + ch} x2={leftPad + cw} y2={topPad + ch} stroke="#555" />
                                            <line x1={leftPad} y1={topPad} x2={leftPad} y2={topPad + ch} stroke="#555" />

                                            {/* X ticks + labels */}
                                            {xTicks.map((tick, i) => (
                                                <g key={`xtick-${i}`}>
                                                    <line x1={tick.x} y1={topPad + ch} x2={tick.x} y2={topPad + ch + 5} stroke="#555" />
                                                    <text x={tick.x} y={topPad + ch + 17} textAnchor="middle" fontSize="10" fill="#333">
                                                        {Number(tick.value).toFixed(2)}
                                                    </text>
                                                </g>
                                            ))}

                                            {/* Y ticks + gridlines + labels */}
                                            {yTicks.map((tick, i) => (
                                                <g key={`ytick-${i}`}>
                                                    <line x1={leftPad - 5} y1={tick.y} x2={leftPad} y2={tick.y} stroke="#555" />
                                                    <line x1={leftPad} y1={tick.y} x2={leftPad + cw} y2={tick.y} stroke="#e0e0e0" />
                                                    <text x={leftPad - 8} y={tick.y + 3} textAnchor="end" fontSize="10" fill="#333">
                                                        {Number(tick.value).toFixed(2)}
                                                    </text>
                                                </g>
                                            ))}

                                            {/* Axis labels */}
                                            <text x={leftPad + cw / 2} y={topPad + ch + 40}
                                                textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333">
                                                {xAxisLabel}
                                            </text>
                                            <text x="12" y={topPad + ch / 2}
                                                textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333"
                                                transform={`rotate(-90 12 ${topPad + ch / 2})`}>
                                                {yAxisLabel}
                                            </text>

                                            {/* Fitted curve */}
                                            <polyline fill="none" stroke="#1976d2" strokeWidth="2" points={linePoints} />
                                            {activeCurvePts.map((p, i) => (
                                                <circle key={`curve-${i}`}
                                                    cx={sx(p.x)} cy={sy(p.y)} r={3}
                                                    fill="#1976d2" stroke="white" strokeWidth={1.5}
                                                    style={{ cursor: "pointer" }}
                                                    onMouseEnter={() => setHoveredPoint({
                                                        type: "Fitted Curve",
                                                        x: p.x, y: p.y,
                                                        svgX: sx(p.x), svgY: sy(p.y)
                                                    })}
                                                    onMouseLeave={() => setHoveredPoint(null)}
                                                />
                                            ))}

                                            {/* Raw RCC data */}
                                            {activeRawPts.map((p, i) => (
                                                <circle key={`raw-${i}`}
                                                    cx={sx(p.x)} cy={sy(p.y)} r={4} fill="#d32f2f"
                                                    style={{ cursor: "pointer" }}
                                                    onMouseEnter={() => setHoveredPoint({
                                                        type: "Raw Data",
                                                        x: p.x, y: p.y,
                                                        svgX: sx(p.x), svgY: sy(p.y)
                                                    })}
                                                    onMouseLeave={() => setHoveredPoint(null)}
                                                />
                                            ))}

                                            {/* IGCC pseudocal crosses */}
                                            {activeIgccPts.map((p, i) => {
                                                const cx = sx(p.x), cy = sy(p.y), arm = 6;
                                                return (
                                                    <g key={`igcc-${i}`}
                                                        style={{ cursor: "pointer" }}
                                                        onMouseEnter={() => setHoveredPoint({
                                                            type: `Pseudocal ${p.label || ""}`.trim(),
                                                            x: p.x, y: p.y, svgX: cx, svgY: cy
                                                        })}
                                                        onMouseLeave={() => setHoveredPoint(null)}>
                                                        <rect x={cx - arm - 2} y={cy - arm - 2}
                                                            width={(arm + 2) * 2} height={(arm + 2) * 2}
                                                            fill="transparent" />
                                                        <line x1={cx - arm} y1={cy} x2={cx + arm} y2={cy}
                                                            stroke="#7b1fa2" strokeWidth={2.25} strokeLinecap="round" />
                                                        <line x1={cx} y1={cy - arm} x2={cx} y2={cy + arm}
                                                            stroke="#7b1fa2" strokeWidth={2.25} strokeLinecap="round" />
                                                    </g>
                                                );
                                            })}

                                            {/* Inline legend (top-right of plot area) */}
                                            {(() => {
                                                const legendX = leftPad + cw - 130;
                                                const legendY = topPad + 6;
                                                const rowH = 16;
                                                const showPseudocal = activeIgccPts.length > 0;
                                                const rows = showPseudocal ? 3 : 2;
                                                return (
                                                    <g>
                                                        <rect x={legendX - 8} y={legendY - 4}
                                                            width={130} height={rowH * rows + 8}
                                                            fill="#ffffff" fillOpacity={0.85}
                                                            stroke="#bbb" strokeWidth={0.5} rx={3} />
                                                        <line x1={legendX} y1={legendY + 6}
                                                            x2={legendX + 18} y2={legendY + 6}
                                                            stroke="#1976d2" strokeWidth={2} />
                                                        <circle cx={legendX + 9} cy={legendY + 6} r={3} fill="#1976d2" />
                                                        <text x={legendX + 24} y={legendY + 9} fontSize="11" fill="#333">
                                                            Fitted Curve
                                                        </text>
                                                        <circle cx={legendX + 9} cy={legendY + rowH + 6} r={4} fill="#d32f2f" />
                                                        <text x={legendX + 24} y={legendY + rowH + 9} fontSize="11" fill="#333">
                                                            Raw Data
                                                        </text>
                                                        {showPseudocal && (
                                                            <>
                                                                <line x1={legendX + 3} y1={legendY + rowH * 2 + 6}
                                                                    x2={legendX + 15} y2={legendY + rowH * 2 + 6}
                                                                    stroke="#7b1fa2" strokeWidth={2.25} strokeLinecap="round" />
                                                                <line x1={legendX + 9} y1={legendY + rowH * 2}
                                                                    x2={legendX + 9} y2={legendY + rowH * 2 + 12}
                                                                    stroke="#7b1fa2" strokeWidth={2.25} strokeLinecap="round" />
                                                                <text x={legendX + 24} y={legendY + rowH * 2 + 9} fontSize="11" fill="#333">
                                                                    Pseudocal
                                                                </text>
                                                            </>
                                                        )}
                                                    </g>
                                                );
                                            })()}

                                            {/* Hover crosshair guides */}
                                            {hoveredPoint && (
                                                <>
                                                    <line x1={sx(hoveredPoint.x)} y1={topPad}
                                                        x2={sx(hoveredPoint.x)} y2={topPad + ch}
                                                        stroke="#333" strokeWidth={1} strokeDasharray="4 4" />
                                                    <line x1={leftPad} y1={sy(hoveredPoint.y)}
                                                        x2={leftPad + cw} y2={sy(hoveredPoint.y)}
                                                        stroke="#333" strokeWidth={1} strokeDasharray="4 4" />
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </svg>

                            {hoveredPoint && (
                                <Box sx={{
                                    position: "absolute",
                                    left: hoveredPoint.svgX,
                                    top: hoveredPoint.svgY,
                                    transform: "translate(10px, -110%)",
                                    backgroundColor: "#222", color: "#fff",
                                    px: 1.25, py: 0.75, borderRadius: 1,
                                    fontSize: 12, pointerEvents: "none",
                                    boxShadow: 3, whiteSpace: "nowrap", zIndex: 2
                                }}>
                                    <div>{hoveredPoint.type}</div>
                                    <div>{xAxisLabel}: {Number(hoveredPoint.x).toFixed(4)}</div>
                                    <div>{yAxisLabel}: {Number(hoveredPoint.y).toFixed(4)}</div>
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>
            </Collapse>
        </Box>
    );
};

export default G06MultiIgcc;
